import React, { useContext, useEffect, useRef, useState } from "react";

import { useParams, useHistory } from "react-router-dom";

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  makeStyles,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@material-ui/core";
import ChatList from "./ChatList";
import ChatMessages from "./ChatMessages";
import { UsersFilter } from "../../components/UsersFilter";
import api from "../../services/api";
import { socketConnection } from "../../services/socket";
import MainContainer from '../../components/MainContainer';
import { has, isObject } from "lodash";

import { AuthContext } from "../../context/Auth/AuthContext";
import withWidth, { isWidthUp } from "@material-ui/core/withWidth";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(2, 3),
    height: "100%",
    overflow: "hidden",
  },
  gridContainer: {
    flex: 1,
    height: "100%",
    borderRadius: 14,
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "#E2E8F0"}`,
    backgroundColor: theme.palette.type === "dark" ? "#1E293B" : "#FFFFFF",
    boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
    overflow: "hidden",
  },
  gridItem: {
    height: "100%",
  },
  gridItemTab: {
    height: "92%",
    width: "100%",
  },
  btnContainer: {
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "#E2E8F0"}`,
  },
  newButton: {
    fontFamily: '"Inter", "Poppins", sans-serif',
    fontWeight: 600,
    fontSize: 14,
    textTransform: "none",
    borderRadius: 10,
    padding: "8px 20px",
    backgroundColor: "#0B3C84",
    color: "#FFFFFF",
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#082D63",
      boxShadow: "0 2px 8px rgba(11,60,132,0.3)",
    },
  },
  tabs: {
    borderBottom: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "#E2E8F0"}`,
    "& .MuiTab-root": {
      fontFamily: '"Inter", "Poppins", sans-serif',
      fontWeight: 600,
      fontSize: 14,
      textTransform: "none",
      minWidth: 120,
      "&.Mui-selected": {
        color: "#0B3C84",
      },
    },
    "& .MuiTabs-indicator": {
      backgroundColor: "#0B3C84",
      height: 3,
      borderRadius: "3px 3px 0 0",
    },
  },
  dialogPaper: {
    borderRadius: 14,
  },
  dialogTitle: {
    fontFamily: '"Inter", "Poppins", sans-serif',
    fontWeight: 700,
    fontSize: 18,
    color: theme.palette.type === "dark" ? "#F1F5F9" : "#1E293B",
  },
  dialogTextField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      fontFamily: '"Inter", "Poppins", sans-serif',
      "& fieldset": {
        borderColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.12)" : "#E2E8F0",
      },
      "&:hover fieldset": {
        borderColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.2)" : "#CBD5E1",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#0B3C84",
      },
    },
  },
  dialogButtonCancel: {
    fontFamily: '"Inter", "Poppins", sans-serif',
    fontWeight: 600,
    fontSize: 14,
    textTransform: "none",
    borderRadius: 10,
    color: theme.palette.type === "dark" ? "#94A3B8" : "#64748B",
  },
  dialogButtonSave: {
    fontFamily: '"Inter", "Poppins", sans-serif',
    fontWeight: 600,
    fontSize: 14,
    textTransform: "none",
    borderRadius: 10,
    padding: "8px 24px",
    backgroundColor: "#0B3C84",
    color: "#FFFFFF",
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#082D63",
      boxShadow: "0 2px 8px rgba(11,60,132,0.3)",
    },
    "&:disabled": {
      backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.1)" : "#E2E8F0",
      color: theme.palette.type === "dark" ? "#64748B" : "#94A3B8",
    },
  },
}));

export function ChatModal({
  open,
  chat,
  type,
  handleClose,
  handleLoadNewChat,
}) {
  const classes = useStyles();
  const [users, setUsers] = useState([]);
  const [title, setTitle] = useState("");

  useEffect(() => {
    setTitle("");
    setUsers([]);
    if (type === "edit") {
      const userList = chat.users.map((u) => ({
        id: u.user.id,
        name: u.user.name,
      }));
      setUsers(userList);
      setTitle(chat.title);
    }
  }, [chat, open, type]);

  const handleSave = async () => {
    try {
      if (type === "edit") {
        await api.put(`/chats/${chat.id}`, {
          users,
          title,
        });
      } else {
        const { data } = await api.post("/chats", {
          users,
          title,
        });
        handleLoadNewChat(data);
      }
      handleClose();
    } catch (err) { }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      PaperProps={{
        className: classes.dialogPaper,
      }}
    >
      <DialogTitle id="alert-dialog-title" className={classes.dialogTitle}>
        {i18n.t("chatInternal.modal.title")}
      </DialogTitle>
      <DialogContent>
        <Grid spacing={2} container>
          <Grid xs={12} style={{ padding: 18 }} item>
            <TextField
              label="Título"
              placeholder="Título"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              variant="outlined"
              size="small"
              fullWidth
              className={classes.dialogTextField}
            />
          </Grid>
          <Grid xs={12} item>
            <UsersFilter
              onFiltered={(users) => setUsers(users)}
              initialUsers={users}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} className={classes.dialogButtonCancel}>
          {i18n.t("chatInternal.modal.cancel")}
        </Button>
        <Button 
          onClick={handleSave} 
          className={classes.dialogButtonSave}
          variant="contained"
          disabled={users===undefined || users.length === 0 || title === null || title === "" || title === undefined}
        >
          {i18n.t("chatInternal.modal.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Chat(props) {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const history = useHistory();

  const [showDialog, setShowDialog] = useState(false);
  const [dialogType, setDialogType] = useState("new");
  const [currentChat, setCurrentChat] = useState({});
  const [chats, setChats] = useState([]);
  const [chatsPageInfo, setChatsPageInfo] = useState({ hasMore: false });
  const [messages, setMessages] = useState([]);
  const [messagesPageInfo, setMessagesPageInfo] = useState({ hasMore: false });
  const [messagesPage, setMessagesPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(0);
  const isMounted = useRef(true);
  const scrollToBottomRef = useRef();
  const socketRef = useRef(null);
  const { id } = useParams();

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (isMounted.current) {
      findChats().then((data) => {
        if (!data || !data.records) {
          return;
        }
        const { records } = data;
        if (records.length > 0) {
          setChats(records);
          setChatsPageInfo(data);

          if (id && records.length) {
            const chat = records.find((r) => r.uuid === id);
            selectChat(chat);
          }
        }
      }).catch((err) => {
        console.log(err);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!currentChat || !currentChat.id) {
      return;
    }
    if (isObject(currentChat) && has(currentChat, "id") && currentChat.id) {
      findMessages(currentChat.id).then(() => {
        if (typeof scrollToBottomRef.current === "function") {
          setTimeout(() => {
            scrollToBottomRef.current();
          }, 300);
        }
      }).catch((err) => {
        console.log(err);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentChat]);

  useEffect(() => {
    if (!user || !user.companyId || !user.id) {
      return;
    }

    // Early return if currentChat is being reset (empty object)
    if (currentChat && Object.keys(currentChat).length === 0) {
      return;
    }

    // Cleanup previous socket if exists
    if (socketRef.current) {
      socketRef.current.off(`company-${user.companyId}-chat-user-${user.id}`);
      socketRef.current.off(`company-${user.companyId}-chat`);
      if (currentChat && currentChat.id) {
        socketRef.current.off(`company-${user.companyId}-chat-${currentChat.id}`);
      }
      socketRef.current.disconnect();
    }

    const companyId = user.companyId;
    const socket = socketConnection({ companyId, userId: user.id });
    socketRef.current = socket;

    const handleUserChatUpdate = (data) => {
      if (data.action === "create") {
        setChats((prev) => [data.record, ...prev]);
      }
      if (data.action === "update") {
        setChats((prev) => {
          const changedChats = prev.map((chat) => {
            if (chat.id === data.record.id) {
              return {
                ...data.record,
              };
            }
            return chat;
          });
          return changedChats;
        });
        // Only update currentChat if it's the chat being updated
        setCurrentChat((prevChat) => {
          if (prevChat && prevChat.id === data.record.id) {
            return data.record;
          }
          return prevChat;
        });
      }
    };

    const handleChatDelete = (data) => {
      if (data.action === "delete") {
        setChats((prev) => prev.filter((c) => c.id !== +data.id));
        setMessages([]);
        setMessagesPage(1);
        setMessagesPageInfo({ hasMore: false });
        setCurrentChat({});
        history.push("/chats");
      }
    };

    socket.on(`company-${companyId}-chat-user-${user.id}`, handleUserChatUpdate);
    socket.on(`company-${companyId}-chat`, handleChatDelete);

    if (isObject(currentChat) && has(currentChat, "id")) {
      const handleCurrentChatMessage = (data) => {
        if (data.action === "new-message") {
          setMessages((prev) => [...prev, data.newMessage]);
          setChats((prev) => {
            return prev.map((chat) => {
              if (chat.id === data.newMessage.chatId) {
                return {
                  ...data.chat,
                };
              }
              return chat;
            });
          });
          scrollToBottomRef.current();
        }

        if (data.action === "update") {
          setChats((prev) => {
            return prev.map((chat) => {
              if (chat.id === data.chat.id) {
                return {
                  ...data.chat,
                };
              }
              return chat;
            });
          });
          scrollToBottomRef.current();
        }
      };

      socket.on(`company-${companyId}-chat-${currentChat.id}`, handleCurrentChatMessage);

      return () => {
        if (socketRef.current) {
          socketRef.current.off(`company-${companyId}-chat-user-${user.id}`, handleUserChatUpdate);
          socketRef.current.off(`company-${companyId}-chat`, handleChatDelete);
          socketRef.current.off(`company-${companyId}-chat-${currentChat.id}`, handleCurrentChatMessage);
          socketRef.current.disconnect();
          socketRef.current = null;
        }
      };
    }

    return () => {
      if (socketRef.current) {
        socketRef.current.off(`company-${companyId}-chat-user-${user.id}`, handleUserChatUpdate);
        socketRef.current.off(`company-${companyId}-chat`, handleChatDelete);
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentChat]);

  const selectChat = (chat) => {
    try {
      if (!chat) {
        return;
      }
      setMessages([]);
      setMessagesPage(1);
      setCurrentChat(chat);
      localStorage.setItem("currentChat", JSON.stringify(chat));
      setTab(1);
    } catch (err) {
      console.log(err);
    }
  };

  const sendMessage = async (contentMessage) => {
    setLoading(true);
    try {
      await api.post(`/chats/${currentChat.id}/messages`, {
        message: contentMessage,
      });
    } catch (err) { }
    setLoading(false);
  };

  const deleteChat = async (chat) => {
    try {
      await api.delete(`/chats/${chat.id}`);
    } catch (err) { }
  };

  const findMessages = async (chatId) => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/chats/${chatId}/messages?pageNumber=${messagesPage}`
      );
      setMessagesPage((prev) => prev + 1);
      setMessagesPageInfo(data);
      setMessages((prev) => [...data.records, ...prev]);
    } catch (err) {
      console.log(err);
    }
    setLoading(false);
  };

  const loadMoreMessages = async () => {
    if (!loading) {
      findMessages(currentChat.id);
    }
  };

  const findChats = async () => {
    try {
      const { data } = await api.get("/chats");
      return data;
    } catch (err) {
      console.log(err);
    }
  };

  const renderGrid = () => {
    return (
      <Grid className={classes.gridContainer} container>
        <Grid className={classes.gridItem} md={3} item>
          <div className={classes.btnContainer}>
            <Button
              onClick={() => {
                setDialogType("new");
                setShowDialog(true);
              }}
              className={classes.newButton}
              variant="contained"
            >
              {i18n.t("chatInternal.new")}
            </Button>
          </div>
          <ChatList
            chats={chats}
            pageInfo={chatsPageInfo}
            loading={loading}
            handleSelectChat={(chat) => selectChat(chat)}
            handleDeleteChat={(chat) => deleteChat(chat)}
            handleEditChat={() => {
              setDialogType("edit");
              setShowDialog(true);
            }}
          />
        </Grid>
        <Grid className={classes.gridItem} md={9} item>
          {isObject(currentChat) && has(currentChat, "id") && (
            <ChatMessages
              chat={currentChat}
              scrollToBottomRef={scrollToBottomRef}
              pageInfo={messagesPageInfo}
              messages={messages}
              loading={loading}
              handleSendMessage={sendMessage}
              handleLoadMore={loadMoreMessages}
            />
          )}
        </Grid>
      </Grid>
    );
  };

  const renderTab = () => {
    return (
      <Grid className={classes.gridContainer} container>
        <Grid md={12} item>
          <Tabs
            value={tab}
            indicatorColor="primary"
            textColor="primary"
            onChange={(e, v) => setTab(v)}
            aria-label="disabled tabs example"
            className={classes.tabs}
          >
            <Tab label="Chats" />
            <Tab label="Mensagens" />
          </Tabs>
        </Grid>
        {tab === 0 && (
          <Grid className={classes.gridItemTab} md={12} item>
            <div className={classes.btnContainer}>
              <Button
                onClick={() => {
                  setDialogType("new");
                  setShowDialog(true);
                }}
                className={classes.newButton}
                variant="contained"
              >
                Novo
              </Button>
            </div>
            <ChatList
              chats={chats}
              pageInfo={chatsPageInfo}
              loading={loading}
              handleSelectChat={(chat) => selectChat(chat)}
              handleDeleteChat={(chat) => deleteChat(chat)}
            />
          </Grid>
        )}
        {tab === 1 && (
          <Grid className={classes.gridItemTab} md={12} item>
            {isObject(currentChat) && has(currentChat, "id") && (
              <ChatMessages
                chat={currentChat}
                scrollToBottomRef={scrollToBottomRef}
                pageInfo={messagesPageInfo}
                messages={messages}
                loading={loading}
                handleSendMessage={sendMessage}
                handleLoadMore={loadMoreMessages}
              />
            )}
          </Grid>
        )}
      </Grid>
    );
  };

  return (
    <>
      <ChatModal
        type={dialogType}
        open={showDialog}
        chat={currentChat}
        handleLoadNewChat={(data) => {
          setMessages([]);
          setMessagesPage(1);
          setCurrentChat(data);
          setTab(1);
          history.push(`/chats/${data.uuid}`);
        }}
        handleClose={() => setShowDialog(false)}
      />
      <MainContainer>
        <Box className={classes.container}>
          {isWidthUp("md", props.width) ? renderGrid() : renderTab()}  
        </Box>
      </MainContainer>
    </>
  );
}

export default withWidth()(Chat);
